import { Link, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import React, { useEffect, useState } from "react";

export default function NavBar() {
  const { pathname } = useLocation();
  const { cart } = useCart();
  const total = cart.reduce((a, i) => a + i.quantity, 0);

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  /* scroll → fond noir */
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  /* méthode lien, re‑utilisée */
  const linkClass = (to: string) => {
    const isActive = pathname.startsWith(to);
    const base = "text-black text-sm";
    const hover = "hover:text-slate-500 transition-all duration-200";

    /* gras & souligné si actif */
    return `${base} ${isActive ? "font-semibold underline" : "font-normal"} ${
      !isActive && hover
    }`;
  };

  /* ───────────────────────── Desktop (> = md) ───────────────────────── */
  const DesktopBar = () => (
    <header
      className="
        bg-[#FBFAF9] text-black border border-slate-300
        py-4 px-6 mx-4 sm:mx-12 lg:mx-24 xl:mx-32 2xl:mx-64 mt-6
        rounded-[25px] shadow-lg sticky top-2 z-50 hidden lg:block
      "
    >
      <nav className="relative  flex items-center justify-between">
        {/* ─── Réseaux à gauche ─── */}
        <div className="flex items-center gap-4">
          <a
            href="https://www.tiktok.com/@same.corp"
            target="_blank"
            rel="noopener noreferrer"
            title="TikTok"
          >
            {/* svg TikTok inchangé */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              className="w-5 h-5 fill-current"
            >
              <path d="M448 209.3v-70.1c-22.8 0-44.8-7.5-62.5-21.6S355.2 84.4 350.4 62.7c-2.1-9.5-3.2-19.3-3.3-29.2h-70.5v281.9c0 23.7-8.4 44.6-25.1 62.5C235 391.9 214.9 400.9 191.5 400.9c-23.9 0-44.2-8.5-60.8-25.4s-25-37.8-25-61.9c0-24.1 8.4-44.9 25.1-62.5 16.7-17.6 37-26.4 60.8-26.4 5.4 0 10.9.6 16.4 1.9V193c-5.2-.7-10.6-1.1-16.1-1.1-35.4 0-65.5 11.5-90.3 34.6S41.8 278.7 41.8 313.4c0 34.9 12.4 64.2 37.3 88 24.9 23.8 56.2 35.6 93.8 35.6 35.3 0 65.4-12.1 90.3-36.4 24.9-24.3 37.3-54.7 37.3-91.4V185.7c19.1 13.8 41.5 22.1 66.3 24.9z" />
            </svg>
          </a>

          <a
            href="https://www.instagram.com/same.corp/"
            target="_blank"
            rel="noopener noreferrer"
            title="Instagram"
          >
            {/* svg Insta outline inchangé */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
            </svg>
          </a>
        </div>

        {/* ─── Menu + logo centré ─── */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-10 whitespace-nowrap">
          <Link to="/articles" className={linkClass("/articles")}>
            Articles
          </Link>
          <Link to="/a-propos" className={linkClass("/a-propos")}>
            À&nbsp;propos
          </Link>

          {/* logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="/images/logo/Same_logo.png"
              alt="SAME"
              className="h-[45px] object-contain"
              loading="lazy"
            />
          </Link>

          <Link to="/uniquepiece" className={linkClass("/uniquepiece")}>
            Pièces&nbsp;uniques
          </Link>
          <Link to="/evenements" className={linkClass("/evenements")}>
            Événements
          </Link>
        </div>

        {/* ─── Panier à droite ─── */}
        <Link to="/cart" className="relative text-lg pb-1 mr-2">
          🛒
          {total > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5">
              {total}
            </span>
          )}
        </Link>
      </nav>
    </header>
  );

  /* ───────────────────────── Mobile (< md) ───────────────────────── */
  const MobileBar = () => (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between h-16 px-4 bg-[#FBFAF9] ${
          scrolled ? "shadow" : ""
        } lg:hidden`}
      >
        {/* hamburger */}
        <button
          aria-label="Menu"
          onClick={() => setOpen(!open)}
          className="text-2xl"
        >
          ☰
        </button>

        {/* logo */}
        <Link to="/">
          <img
            src="/images/logo/Same_logo.png"
            alt="SAME"
            className="h-8 ml-7"
            loading="lazy"
          />
        </Link>

        {/* réseaux + panier */}
        <div className="flex items-center space-x-3 text-sm font-semibold">
          {/* TikTok icon */}
          <a
            href="https://www.tiktok.com/@same.corp"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok"
            title="TikTok"
            className="hover:underline"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              className="w-5 h-5 fill-current"
            >
              <path d="M448 209.3v-70.1c-22.8 0-44.8-7.5-62.5-21.6S355.2 84.4 350.4 62.7c-2.1-9.5-3.2-19.3-3.3-29.2h-70.5v281.9c0 23.7-8.4 44.6-25.1 62.5C235 391.9 214.9 400.9 191.5 400.9c-23.9 0-44.2-8.5-60.8-25.4s-25-37.8-25-61.9c0-24.1 8.4-44.9 25.1-62.5 16.7-17.6 37-26.4 60.8-26.4 5.4 0 10.9.6 16.4 1.9V193c-5.2-.7-10.6-1.1-16.1-1.1-35.4 0-65.5 11.5-90.3 34.6S41.8 278.7 41.8 313.4c0 34.9 12.4 64.2 37.3 88 24.9 23.8 56.2 35.6 93.8 35.6 35.3 0 65.4-12.1 90.3-36.4 24.9-24.3 37.3-54.7 37.3-91.4V185.7c19.1 13.8 41.5 22.1 66.3 24.9z" />
            </svg>
          </a>

          {/* Insta icon */}
          <a
            href="https://www.instagram.com/same.corp/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            title="Instagram"
            className="hover:underline"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none" /* pas de remplissage */
              stroke="currentColor" /* contour = couleur du texte */
              strokeWidth="2"
            >
              {/* carr� arrondi */}
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              {/* objectif */}
              <circle cx="12" cy="12" r="4" />
              {/* petit point en haut à droite */}
              <circle cx="17" cy="7" r="1" fill="currentColor" stroke="none" />
            </svg>
          </a>

          {/* panier */}
          <Link to="/cart" className="relative text-lg pb-1">
            🛒
            {total > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full px-1.5">
                {total}
              </span>
            )}
          </Link>
        </div>
      </header>

      {/* menu déroulant */}
      {open && (
        <nav className="xl:hidden fixed top-16 left-0 right-0 bg-[#FBFAF9] border-t shadow z-40">
          <Link
            to="/articles"
            onClick={() => setOpen(false)}
            className={
              linkClass("/articles") +
              " block py-4 px-6 !text-black font-semibold"
            }
          >
            Articles
          </Link>
          <Link
            to="/a-propos"
            onClick={() => setOpen(false)}
            className={
              linkClass("/a-propos") +
              " block py-4 px-6 !text-black font-semibold"
            }
          >
            À propos
          </Link>
          <Link
            to="/evenements"
            onClick={() => setOpen(false)}
            className={
              linkClass("/evenements") +
              " block py-4 px-6 !text-black font-semibold"
            }
          >
            Evenements
          </Link>
          <Link
            to="/uniquepiece"
            onClick={() => setOpen(false)}
            className={
              linkClass("/uniquepiece") +
              " block py-4 px-6 !text-black font-semibold"
            }
          >
            Pièces uniques
          </Link>
        </nav>
      )}
    </>
  );

  return (
    <>
      <DesktopBar />
      <MobileBar />
    </>
  );
}
